import React, { memo, useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { OPERATOR_EQUALS, creators, print } from '@atlaskit/jql-ast';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { SprintInlineEditViewIsEditable } from '@atlassian/jira-issue-field-sprint-inline-edit-full';
import ListCellWithPopup, {
	LinkItem,
	LinkItemPopupContentWrapper,
} from '@atlassian/jira-list-with-popup';
import type { sprint_nativeIssueTable_SprintCell$key } from '@atlassian/jira-relay/src/__generated__/sprint_nativeIssueTable_SprintCell.graphql';
import type { sprint_nativeIssueTable_SprintCellInternal_fieldRef$key as SprintCellInternalKey } from '@atlassian/jira-relay/src/__generated__/sprint_nativeIssueTable_SprintCellInternal_fieldRef.graphql';
import type { sprint_nativeIssueTable_SprintCellInternalOld_fieldRef$key as SprintCellInternalOldKey } from '@atlassian/jira-relay/src/__generated__/sprint_nativeIssueTable_SprintCellInternalOld_fieldRef.graphql';

import type { ProjectKey } from '@atlassian/jira-shared-types';
// eslint-disable-next-line jira/import/no-restricted-paths
import { useRestrictSprints } from '../../../../controllers/restrict-sprints';
// eslint-disable-next-line jira/import/no-restricted-paths
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader';
import { escapeBackslashes, isNonNullish } from '../../../utils';
import ErrorCell from '../../error-cell';
import messages from './messages';

export type Props = {
	fieldRef: sprint_nativeIssueTable_SprintCell$key;
	issueSearchBaseUrl: string;
	jqlTerm: string;
	projectKey: ProjectKey;
};

/**
 * Expected JQL Output: `${jqlTerm} = ${name}`
 */
const getLinkJql = (jqlTerm: string, name: string) => {
	const jast = creators.jast(
		creators.query(
			creators.terminalClause(
				creators.field(escapeBackslashes(jqlTerm)),
				creators.operator(OPERATOR_EQUALS),
				creators.valueOperand(escapeBackslashes(name)),
			),
		),
	);

	return print(jast, {
		operatorCase: ff('nin-better-jql-builder-basic_m0m6q') ? 'upper' : undefined,
	});
};

/**
 * Expected JQL Output: `${jqlTerm} = ${sprintId}`
 */
const getSprintJql = (jqlTerm: string, sprintId: number) => {
	const jast = creators.jast(
		creators.query(
			creators.terminalClause(
				creators.field(escapeBackslashes(jqlTerm)),
				creators.operator(OPERATOR_EQUALS),
				creators.valueOperand(String(sprintId)),
			),
		),
	);

	return print(jast, {
		operatorCase: ff('nin-better-jql-builder-basic_m0m6q') ? 'upper' : undefined,
	});
};

export const SprintCell = memo<Props>(({ fieldRef, projectKey, issueSearchBaseUrl, jqlTerm }) => {
	const data = useFragment<sprint_nativeIssueTable_SprintCell$key>(
		/* eslint-disable jira/relay/graphql-naming */
		graphql`
			fragment sprint_nativeIssueTable_SprintCell on JiraSprintField
			@argumentDefinitions(
				includeSprintInlineEdit: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/sprint-inline-edit.relayprovider"
				}
				isInlineEditingEnabled: { type: "Boolean!" }
			) {
				...sprint_nativeIssueTable_SprintCellInternalOld_fieldRef
					@skip(if: $includeSprintInlineEdit)
				...sprint_nativeIssueTable_SprintCellInternal_fieldRef
					@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
					@include(if: $includeSprintInlineEdit)
			}
		`,
		fieldRef,
		/* eslint-enable jira/relay/graphql-naming */
	);

	if (ff('one-issue-table-sprint-inline-edit')) {
		return (
			<SprintCellInternal
				fieldRef={data}
				issueSearchBaseUrl={issueSearchBaseUrl}
				jqlTerm={jqlTerm}
				projectKey={projectKey}
			/>
		);
	}
	return (
		<SprintCellInternalOld
			fieldRef={data}
			issueSearchBaseUrl={issueSearchBaseUrl}
			jqlTerm={jqlTerm}
		/>
	);
});

type PropsInternal = Omit<Props, 'fieldRef'> & {
	fieldRef: SprintCellInternalKey;
};

const SprintCellInternal = ({
	fieldRef,
	issueSearchBaseUrl,
	jqlTerm,
	projectKey,
}: PropsInternal) => {
	const data = useFragment<SprintCellInternalKey>(
		graphql`
			fragment sprint_nativeIssueTable_SprintCellInternal_fieldRef on JiraSprintField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...sprint_issueFieldSprintInlineEditFull_SprintInlineEditViewIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable = 'lazyIsEditableInIssueView' in data ? data.lazyIsEditableInIssueView : false;
	useLazyLoadEditability(data.id, isEditable);

	const [restrictSprints, { onRestrictSprints }] = useRestrictSprints();

	const getUrl = useCallback(
		(sprintId: number): string => {
			const jql = getSprintJql(jqlTerm, sprintId);
			return `${issueSearchBaseUrl}?jql=${encodeURIComponent(jql)}`;
		},
		[issueSearchBaseUrl, jqlTerm],
	);

	return (
		<SprintInlineEditViewIsEditable
			spacing={ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? 'default' : undefined}
			editViewPopup={ff('nin-resizable-columns_8z4eo')}
			editViewPopupAlignBlock={ff('nin-resizable-columns_8z4eo') ? 'center' : undefined}
			fragmentRef={data}
			getUrl={getUrl}
			isEditable={isEditable ?? false}
			menuPosition={ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? 'fixed' : undefined}
			onRestrictSprints={onRestrictSprints}
			projectKey={projectKey}
			readViewFitContainerHeight
			restrictSprints={restrictSprints}
		/>
	);
};

type PropsInternalOld = Omit<Props, 'fieldRef' | 'projectKey'> & {
	fieldRef: SprintCellInternalOldKey;
};

const SprintCellInternalOld = ({ fieldRef, issueSearchBaseUrl, jqlTerm }: PropsInternalOld) => {
	const { formatMessage } = useIntl();
	const { selectedSprintsConnection } = useFragment<SprintCellInternalOldKey>(
		graphql`
			fragment sprint_nativeIssueTable_SprintCellInternalOld_fieldRef on JiraSprintField {
				selectedSprintsConnection {
					edges {
						node {
							name
						}
					}
				}
			}
		`,
		fieldRef,
	);

	const sprints = selectedSprintsConnection?.edges;

	const filteredSprints = useMemo(
		() =>
			sprints
				?.map((edge) =>
					edge?.node?.name != null
						? {
								key: edge.node.name,
								name: edge.node.name,
								href: `${issueSearchBaseUrl}?jql=${encodeURIComponent(
									getLinkJql(jqlTerm, edge.node.name),
								)}`,
							}
						: null,
				)
				.filter(isNonNullish) ?? [],
		[sprints, issueSearchBaseUrl, jqlTerm],
	);
	if (!sprints || sprints.length !== filteredSprints.length) {
		return <ErrorCell cellType="sprint" reason="Missing required fields" />;
	}

	return (
		<ListCellWithPopup
			items={filteredSprints}
			ItemComponent={LinkItem}
			PopupContentWrapper={LinkItemPopupContentWrapper}
			showMoreTooltip={formatMessage(messages.triggerButton)}
		/>
	);
};
